/* ------------------------------------------------------------------
   RAVA - WISHLIST GUARD

   Nothing should reach the wishlist unless a customer deliberately
   taps the button.

   The add-to-wishlist control is a full width, transparent strip
   directly under the buy button. On a phone a scroll that begins on
   that strip can still end in a click: the browser only cancels the
   click if it decides the gesture was a drag, and a slow flick that
   travels a short distance does not always qualify. The result is an
   item appearing in the wishlist while the customer believes they
   only scrolled.

   So: remember where a touch or a pointer press started, and if the
   finger travelled, or the page scrolled underneath it, swallow the
   click that follows. A genuine tap moves almost nothing and passes
   through untouched.

   Clicks the browser did not originate are dropped outright. Nothing
   legitimate synthesises a click on this button.

   Listeners are attached in the capture phase on the document so this
   runs before the app's own handler, and so buttons the app injects
   later are covered without needing to rebind.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  // How far a finger may travel and still count as a tap.
  var SLOP = 10;

  var start = null;

  function isWishlistTarget(node) {
    if (!node || !node.closest) return false;
    return !!node.closest(
      '#swym-atw-pdp-button, .swym-atw-button-container, ' +
      '.swym-add-to-wishlist-view-product, [data-swym-add-to-wishlist], ' +
      '.swym-button-bar button, button.atw-button'
    );
  }

  function remember(x, y) {
    start = { x: x, y: y, scrollY: window.scrollY, moved: false, at: Date.now() };
  }

  document.addEventListener('touchstart', function (e) {
    if (!e.touches || !e.touches.length) return;
    remember(e.touches[0].clientX, e.touches[0].clientY);
  }, { capture: true, passive: true });

  document.addEventListener('touchmove', function (e) {
    if (!start || !e.touches || !e.touches.length) return;
    var t = e.touches[0];
    if (Math.abs(t.clientX - start.x) > SLOP || Math.abs(t.clientY - start.y) > SLOP) {
      start.moved = true;
    }
  }, { capture: true, passive: true });

  document.addEventListener('mousedown', function (e) {
    remember(e.clientX, e.clientY);
  }, true);

  document.addEventListener('mousemove', function (e) {
    if (!start) return;
    if (Math.abs(e.clientX - start.x) > SLOP || Math.abs(e.clientY - start.y) > SLOP) {
      start.moved = true;
    }
  }, true);

  document.addEventListener('click', function (e) {
    if (!isWishlistTarget(e.target)) return;

    var reason = null;

    if (!e.isTrusted) {
      reason = 'synthetic click';
    } else if (start) {
      // The page moving under the finger is the clearest sign of a scroll.
      if (start.moved) reason = 'pointer travelled';
      else if (Math.abs(window.scrollY - start.scrollY) > SLOP) reason = 'page scrolled';
    }

    if (!reason) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    if (window.console && console.debug) {
      console.debug('[rava] wishlist click ignored: ' + reason);
    }
  }, true);

  /* ----------------------------------------------------------------
     English wording

     The wishlist app takes its language from Shopify's own locale, and
     this store publishes only Hungarian, so it writes Hungarian for
     everyone. The site's English mode is a class on <body> that the app
     knows nothing about, so its wording is translated here instead.

     Once English is published as a real Shopify language and visitors
     reach /en/ pages, the app will translate itself and this becomes a
     no-op: it only ever rewrites text it recognises.
     ---------------------------------------------------------------- */

  var PHRASES = {
    'Hozzáadás a kedvencekhez': 'Add to wishlist',
    'Hozzáadva a kedvencekhez': 'Added to wishlist',
    'Hozzáadás a kívánságlistához': 'Add to wishlist',
    'Hozzáadva a kívánságlistához': 'Added to wishlist',
    'Kívánságlista': 'Wishlist',
    'Kedvencek': 'Wishlist',
    'Kedvenceim': 'My wishlist',
    'KEDVENCEIM': 'MY WISHLIST'
  };

  /* Applied in both languages. The app leaves some of its own copy in English
     whatever the store language, and that copy uses em dashes, which the client
     does not want anywhere on the site. */
  var PHRASES_ANY = {
    'Sign up — it takes a minute': 'Sign up, it takes a minute'
  };

  var TEXT_SELECTOR =
    '#swym-atw-pdp-button-text, .atw-button-text, ' +
    '#swym-wishlist-title, .swym-storefront-layout-title, ' +
    '.swym-storefront-layout-empty-wishlist-title, ' +
    // The floating launcher's own label, which sits above everything else.
    '#notepad-anchor-title, #swym-anchor a, ' +
    // The empty-wishlist panel, whose sign-up line carries an em dash.
    '.swym-storefront-layout-empty-state-signup-link, ' +
    '.swym-storefront-layout-empty-wishlist-description, ' +
    '.swym-storefront-layout-empty-state-notice-text';

  function translateWishlist() {
    var english = document.body.classList.contains('en-lang');
    var nodes = document.querySelectorAll(TEXT_SELECTOR);

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var current = (el.textContent || '').trim();

      var replacement = PHRASES_ANY[current];
      if (!replacement && english) replacement = PHRASES[current];
      if (!replacement) continue;

      /* Only the text is rewritten, never the element's contents.
         Setting textContent here destroyed the heart icon: the floating
         launcher is <a><i class="swym-icon"></i>Kedvenceim</a>, the icon
         carries no text of its own, and replacing textContent threw it away
         and left a bare black square. */
      var replaced = false;
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var node;
      while ((node = walker.nextNode())) {
        if (!(node.nodeValue || '').trim()) continue;
        node.nodeValue = replaced ? '' : replacement;
        replaced = true;
      }
      // No text node at all, so there is nothing to lose by writing one.
      if (!replaced && !el.children.length) el.textContent = replacement;
    }
  }

  document.addEventListener('DOMContentLoaded', translateWishlist);
  window.addEventListener('load', translateWishlist);

  // The app rewrites the button as its state changes, and injects the
  // drawer long after load, so watch rather than translate once.
  new MutationObserver(translateWishlist).observe(document.documentElement, {
    childList: true, subtree: true, characterData: true
  });

  // Switching language only changes a class on <body>, which is an attribute
  // change and so invisible to the observer above.
  new MutationObserver(translateWishlist).observe(document.body, {
    attributes: true, attributeFilter: ['class']
  });

  /* The app renders its counter badge even when the list is empty, so a
     bare orange dot follows the customer around the site. Marked, rather
     than hidden here, so the stylesheet decides how it looks. */
  function markBadge() {
    var badge = document.querySelector('.swym-anchor-badge');
    if (!badge) return;
    var count = parseInt((badge.textContent || '').trim(), 10);
    badge.setAttribute('data-rava-empty', (!count || count < 1) ? 'true' : 'false');
  }

  function watchBadge() {
    markBadge();
    var badge = document.querySelector('.swym-anchor-badge');
    if (!badge) return false;
    new MutationObserver(markBadge).observe(badge, { childList: true, characterData: true, subtree: true });
    return true;
  }

  if (!watchBadge()) {
    // The app injects its launcher after load; give it a bounded window.
    var tries = 0;
    var poll = setInterval(function () {
      tries++;
      if (watchBadge() || tries > 40) clearInterval(poll);
    }, 500);
  }
})();
